import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { TabPermissions } from '../types';
import { useAppContext } from './AppContext';
import { getPermissions } from '../services/systemApi';

/**
 * RBAC Context
 *
 * Provides the current user's role and tab permissions to all child components.
 * After authentication completes, fetches resolved permissions from the API.
 * Falls back to extracting cognito:groups from the ID token if the API call fails.
 *
 * Requirements: 7.1, 7.2
 */

export interface RBACContextType {
  role: string | null;
  permissions: TabPermissions | null;
  loading: boolean;
  permissionsFailed: boolean;
}

// When RBAC API is unavailable or no roles are configured yet,
// grant all permissions to preserve pre-RBAC behavior and avoid
// locking users out of the dashboard.
const fallbackPermissions: TabPermissions = {
  logs: true,
  monitoring: true,
  configuration: true,
  system: true,
};

const RBACContext = createContext<RBACContextType | undefined>(undefined);

interface RBACProviderProps {
  children: ReactNode;
}

export const RBACProvider: React.FC<RBACProviderProps> = ({ children }) => {
  const { isAuthenticated, user, isInitializing } = useAppContext();
  const [role, setRole] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<TabPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissionsFailed, setPermissionsFailed] = useState(false);

  useEffect(() => {
    // Wait for auth initialization to complete
    if (isInitializing) {
      return;
    }

    // If not authenticated, reset RBAC state
    if (!isAuthenticated) {
      setRole(null);
      setPermissions(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchPermissions() {
      setLoading(true);
      try {
        const data = await getPermissions();
        if (!cancelled) {
          setPermissions(data.permissions);
          setRole(data.role ?? user?.groups?.[0] ?? null);
          setPermissionsFailed(false);
        }
      } catch (error) {
        console.error('[RBAC] Failed to fetch permissions, falling back to full access:', error);
        if (!cancelled) {
          const userRole = user?.groups?.[0] ?? null;
          setRole(userRole);
          setPermissions(fallbackPermissions);
          setPermissionsFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchPermissions();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isInitializing, user]);

  const value: RBACContextType = {
    role,
    permissions,
    loading,
    permissionsFailed,
  };

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
};

export const useRBAC = (): RBACContextType => {
  const context = useContext(RBACContext);
  if (context === undefined) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
};
